import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { projectService } from '@/services/projectService'

const categoryLabels = { todo: 'To Do', in_progress: 'In Progress', done: 'Done' }

const SortableStatusRow = ({ status, onNameChange, onCategoryChange, onDelete, saving }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 bg-background border rounded-md">
      <button {...attributes} {...listeners} className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing">
        <GripVertical className="h-4 w-4" />
      </button>

      <Input
        value={status.name}
        onChange={(e) => onNameChange(status.id, e.target.value)}
        onBlur={() => onNameChange(status.id, status.name, true)}
        className="h-8 flex-1"
      />

      <Select
        value={status.category}
        onValueChange={(v) => onCategoryChange(status.id, v)}
      >
        <SelectTrigger className="h-8 w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(categoryLabels).map(([k, v]) => (
            <SelectItem key={k} value={k}>{v}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={() => onDelete(status)}
        disabled={saving}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

const StatusManager = ({ projectId, initialStatuses = [] }) => {
  const queryClient = useQueryClient()
  const [statuses, setStatuses] = useState(initialStatuses)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [newName, setNewName] = useState('')
  const [newCategory, setNewCategory] = useState('todo')

  useEffect(() => { setStatuses(initialStatuses) }, [initialStatuses])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return
    const oldIndex = statuses.findIndex(s => s.id === active.id)
    const newIndex = statuses.findIndex(s => s.id === over.id)
    const reordered = arrayMove(statuses, oldIndex, newIndex).map((s, i) => ({ ...s, position: i }))
    setStatuses(reordered)
    try {
      await projectService.reorderStatuses(projectId, reordered.map(s => ({ id: s.id, position: s.position })))
      queryClient.invalidateQueries(['projects', projectId, 'statuses'])
    } catch {
      toast.error('Failed to reorder statuses')
      setStatuses(initialStatuses)
    }
  }

  const handleNameChange = async (id, name, save = false) => {
    setStatuses(prev => prev.map(s => s.id === id ? { ...s, name } : s))
    if (save) {
      try {
        await projectService.updateStatus(projectId, id, { name })
        queryClient.invalidateQueries(['projects', projectId, 'statuses'])
      } catch {
        toast.error('Failed to update status name')
      }
    }
  }

  const handleCategoryChange = async (id, category) => {
    setStatuses(prev => prev.map(s => s.id === id ? { ...s, category } : s))
    try {
      await projectService.updateStatus(projectId, id, { category })
      queryClient.invalidateQueries(['projects', projectId, 'statuses'])
    } catch {
      toast.error('Failed to update category')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setSaving(true)
    try {
      await projectService.deleteStatus(projectId, deleteTarget.id)
      setStatuses(prev => prev.filter(s => s.id !== deleteTarget.id))
      queryClient.invalidateQueries(['projects', projectId, 'statuses'])
      toast.success('Status deleted')
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete status'
      toast.error(msg)
    } finally {
      setSaving(false)
      setDeleteTarget(null)
    }
  }

  const handleAddStatus = async () => {
    if (!newName.trim()) return toast.error('Status name is required')
    setSaving(true)
    try {
      const res = await projectService.createStatus(projectId, { name: newName.trim(), category: newCategory })
      setStatuses(prev => [...prev, res.data.status])
      queryClient.invalidateQueries(['projects', projectId, 'statuses'])
      setNewName('')
      setNewCategory('todo')
      toast.success('Status added')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add status')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={statuses.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {statuses.map(status => (
              <SortableStatusRow
                key={status.id}
                status={status}
                onNameChange={handleNameChange}
                onCategoryChange={handleCategoryChange}
                onDelete={setDeleteTarget}
                saving={saving}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add new status */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New status name"
          className="h-8 flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
        />
        <Select value={newCategory} onValueChange={setNewCategory}>
          <SelectTrigger className="h-8 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(categoryLabels).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8" onClick={handleAddStatus} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete status "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.taskCount > 0
                ? `This status has ${deleteTarget.taskCount} task(s). Reassign them before deleting.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {(!deleteTarget?.taskCount || deleteTarget.taskCount === 0) && (
              <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default StatusManager
